import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  GetManyParams,
  GetManyResult,
  DeleteParams,
} from 'react-admin'
import { PRINT_BARCODE_ITEMS_URL } from '../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import filterMapper from './print-barcode-items-filter.mapper'
import { mapSortPrintBarcodeItemsParam } from './print-barcode-items-sort.mapper'
import { PrintAnchor } from '../../enum/PrintAnchor'
import { printBarcodeItemDto } from '../../dto/print-barcode-item.dto'
import { PrintBarcodeDirection } from '../../enum/PrintBarcodeDirection'
import { PrintBarcodeType } from '../../enum/PrintBarcodeType'
import { PrintParameterTypes } from '../../enum/PrintParameterTypes'
import printItemParameterProvider from '../print-item-parameters/print-item-parameters.provider'
import { ResourceName } from '../../ResourceName'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<printBarcodeItemDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortPrintBarcodeItemsParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<printBarcodeItemDto[]>(
      `${PRINT_BARCODE_ITEMS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<printBarcodeItemDto>> => {
    const data = await get<printBarcodeItemDto>(
      PRINT_BARCODE_ITEMS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource,
    { data }: CreateParams<CreatePrintBarcodeItemRequest>,
  ): Promise<CreateResult<printBarcodeItemDto>> => {
    const response = await post<
      CreatePrintBarcodeItemRequest[],
      printBarcodeItemDto[]
    >(PRINT_BARCODE_ITEMS_URL, [data])
    const printBarcodeItem = response[0]
    if (data.newParamKey && data.newParamType && printBarcodeItem.id)
      await printItemParameterProvider.create(
        ResourceName.PRINT_ITEM_PARAMETERS,
        {
          data: {
            key: data.newParamKey,
            options: data?.newParamOptions,
            type: data.newParamType,
            printItemId: printBarcodeItem.id,
          },
        },
      )
    return {
      data: printBarcodeItem,
    }
  },
  update: async (
    resource: string,
    { id, data }: UpdateParams,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      printBarcodeItemDto
    >(PRINT_BARCODE_ITEMS_URL, {
      id,
      ...data,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<printBarcodeItemDto>(
      PRINT_BARCODE_ITEMS_URL,
      `/Delete/${id}`,
    )

    return Promise.resolve({ data: deleted })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<printBarcodeItemDto>> => {
    const data = await get<printBarcodeItemDto[]>(
      PRINT_BARCODE_ITEMS_URL,
      `/GetByConditions/o=>new int[] {${ids.toString()}}.Contains(o.Id)`,
    )
    return Promise.resolve({ data })
  },
} as DataProvider

export interface CreatePrintBarcodeItemRequest {
  readonly xPosition: number
  readonly yPosition: number
  readonly anchor: PrintAnchor
  readonly priority: number
  readonly printLayoutId: number
  readonly code: string
  readonly barcodeDirection: PrintBarcodeDirection
  readonly barcodeType: PrintBarcodeType
  readonly height: number
  readonly width: number
  readonly newParamKey: string
  readonly newParamOptions: string
  readonly newParamType: PrintParameterTypes
}

export default provider
